import { useState } from 'react';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import styled from 'styled-components';
import { CredusText, CredusDropDown } from 'ui';
import { HiOutlineDotsHorizontal } from 'react-icons/hi';

import { PublishedTests } from '../../../../../contexts/TestContext';
import TestConfirmAction from '../modals/TestConfirmAction';
import testApi from '../../../../../api/test';
import { Refetch } from '../TestData';

export interface TagProps {
  background: string;
  pad: string;
  radius?: string;
}

interface TestDataTableProps {
  setIsBatchAssign: React.Dispatch<React.SetStateAction<boolean>>;
  activeTestId: number;
  setActiveTestId: React.Dispatch<React.SetStateAction<number>>;
  setAssignUser: React.Dispatch<React.SetStateAction<boolean>>;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;
  setShowActionSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  testIds: number[];
  refetch: Refetch;
  setTestIds: React.Dispatch<React.SetStateAction<number[]>>;
  testData: PublishedTests[];
}

const TestDataTable = ({
  setIsBatchAssign,
  activeTestId,
  setSuccessMessage,
  setShowActionSuccess,
  setActiveTestId,
  setAssignUser,
  testIds,
  setTestIds,
  testData,
}: TestDataTableProps) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showConfirmAction, setShowConfirmAction] = useState(false);
  const [confirmFunction, setConfirmFunction] = useState<() => Promise<void>>();
  const [text, setText] = useState('');

  const handleCheckAll = () => {
    const testIdArray = testData?.map((data) => data.testId);
    if (testIdArray) {
      if (testIds.length == testIdArray?.length) {
        setIsBatchAssign(false);
        return setTestIds([]);
      }
      setIsBatchAssign(true);
      setTestIds([...testIdArray]);
    }
  };

  const handleCheck = (e: CheckboxChangeEvent) => {
    setTestIds((prev) =>
      testIds?.includes(e.target.value)
        ? [...prev.filter((id) => id !== e.target.value)]
        : [...prev, e.target.value]
    );
  };

  const handleActionDropDown = (testId: number) => {
    setActiveTestId(testId);
    setShowDropDown((prev) => !prev);
  };

  const activateTest = async () => {
    setConfirmLoading(true);
    setSuccessMessage('Test activated successfully');
    const res = await testApi.activateTest(activeTestId);
    if (res) {
      setShowActionSuccess(true);
      setConfirmLoading(false);
      setShowConfirmAction(false);
    }
  };
  const deleteTest = async () => {
    setConfirmLoading(true);
    setSuccessMessage('Test deleted successfully');
    const res = await testApi.deleteTest(activeTestId);
    if (res) {
      setShowActionSuccess(true);
      setConfirmLoading(false);
      setShowConfirmAction(false);
    }
  };
  const handleAssign = () => {
    setSuccessMessage('Test has  been successfully  assigned to a period');
    setShowDropDown(false);
    setAssignUser(true);
  };
  const handleSuspend = async () => {
     setConfirmLoading(true);
     setSuccessMessage('Test has been suspended');
     const res = await testApi.suspendTest(activeTestId);
     if (res) {
       setShowActionSuccess(true);
       setConfirmLoading(false);
       setShowConfirmAction(false);
     }
    return;
  };
  const handleAction = (type: string, data: PublishedTests) => {
    const { code } = data;
    setShowDropDown(false);
    setText(
      type === 'Activate'
        ? `You’re about to activate Economics test (${code}). Do you want to continue?`
        : `You’re about to delete Economics test (${code}). 
        Kindly note that this will remove it from the system. Do you want to continue?`
    );
    setConfirmFunction(
      type == 'Activate' ? () => activateTest : () => deleteTest
    );
    setShowConfirmAction(true);
  };

  return (
    <div>
      {showConfirmAction && (
        <TestConfirmAction
          showActionConfirmation={showConfirmAction}
          setShowActionConfirmation={setShowConfirmAction}
          loading={confirmLoading}
          text={text}
          action={confirmFunction}
        />
      )}

      <Table>
        <tr>
          <Th>
            <Checkbox onChange={handleCheckAll} />
          </Th>
          <Th>
            <CredusText size="md">Test Name</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Test Code</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Category</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Created by</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Test Date</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Test Time</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Status</CredusText>
          </Th>
          <Th>
            <CredusText size="md">Action</CredusText>
          </Th>
        </tr>
        {testData?.map((data) => {
          const {
            date,
            time,
            code,
            status,
            createdByName,
            testId,
            category,
            subject,
          } = data;
          const actionItems = [
            <CredusText size="xs" onClick={handleAssign}>
              Assign
            </CredusText>,
            <CredusText
              size="xs"
              onClick={() =>
                status == 'Active'
                  ? handleSuspend()
                  : handleAction('Activate', data)
              }
            >
              {status == 'Active' ? 'Suspend' : 'Activate'}
            </CredusText>,
            <CredusText size="xs" onClick={() => handleAction('', data)}>
              Delete
            </CredusText>,
          ];
          const assignedItems = actionItems.filter(
            (item, index) => index !== 0
          );
          return (
            <tr key={testId}>
              <Td>
                <TdDiv>
                  <Checkbox
                    value={testId}
                    checked={testIds?.includes(testId)}
                    onChange={handleCheck}
                  />
                </TdDiv>
              </Td>
              <Td>
                <CredusText size="sm">{subject}</CredusText>
              </Td>
              <Td>
                <CredusText size="sm">{code}</CredusText>
              </Td>
              <Td>
                <CredusText size="sm">{category}</CredusText>
              </Td>
              <Td>
                <CredusText size="sm">{createdByName}</CredusText>
              </Td>
              <Td>
                <CredusText size="sm">{date}</CredusText>
              </Td>
              <Td>
                <CredusText size="sm">{time}</CredusText>
              </Td>
              <Td>
                <Tag
                  background={
                    status == 'Created'
                      ? '#FFF0BB'
                      : status == 'Assigned'
                      ? '#EDE7FB'
                      : status == 'Active'
                      ? '#FBE7E7'
                      : '#D5F8D8'
                  }
                  radius="5px"
                  pad="10px"
                >
                  <CredusText size="sm">
                    {status == 'Created'
                      ? 'Test Created'
                      : status == 'Assigned'
                      ? 'Test Assigned'
                      : status == 'Active'
                      ? 'Test Active'
                      : 'Test Completed'}
                  </CredusText>
                </Tag>
              </Td>
              <Td>
                <Div>
                  <HiOutlineDotsHorizontal
                    color="#ABABAB"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleActionDropDown(testId)}
                  />
                  {showDropDown && activeTestId == testId && (
                    <CredusDropDown
                      items={status == 'Assigned' ? assignedItems : actionItems}
                      width={'100px'}
                    />
                  )}
                </Div>
              </Td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
};

export default TestDataTable;

const Table = styled.table`
  width: 100%;
`;

const Th = styled.th`
  background: #ffeae7;
  padding: 1rem 1.2rem;
  min-width: 44px;
`;
const Td = styled.td`
  /* background: #fbfbfb; */
  padding: 1rem 1.2rem;
  min-width: 44px;
`;

const Div = styled.div`
  position: relative;
`;

const TdDiv = styled.div`
  display: flex;
  justify-content: center;
`;

export const Tag = styled.div<TagProps>`
  background: ${(props) => props.background};
  padding: ${(props) => props.pad};
  border-radius: ${(props) => props.radius};
  display: flex;
  justify-content: center;
  align-items: center;
`;
